import React, { useState } from 'react';
import { usePasswordRecoveryStore } from '@stores/auth';
import { useClientStore } from '@stores/client';
import { AlertTriangle, Loader2, Eye, EyeOff } from 'lucide-react';
import { maskEmail } from '@/client/utils/helpers';
import { toast } from 'react-toastify';
import { BrandMark } from '@/components/brand-mark';

type Inputs = {
  pin?: string;
  password?: string;
  confirm?: string;
};

interface PasswordRecoveryProps {
  onOpenChange: (open: any) => void;
}

const PasswordRecovery: React.FC<PasswordRecoveryProps> = ({ onOpenChange }) => {
  const { request } = useClientStore();
  const {
    usePhone,
    inputValue,
    setUsePhone,
    setInputValue,
    resetForm
  } = usePasswordRecoveryStore();

  const [inputs, setInputs] = useState<Inputs>({});
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function initiateProcess() {
    if (!inputValue.trim()) {
      toast.error(`${usePhone ? 'Phone number' : 'Email address'} is required`);
      return;
    }

    try {
      setShowForm(false);
      setInitLoading(true);

      const payload = {
        change_type: 'recover',
        [usePhone ? 'phone' : 'email']: inputValue
      };

      const response = await request<{ status: number; data: any }>(
        `/auth/v2/user/account/verification/${usePhone ? 'phone' : 'email'}/anon`,
        'POST',
        payload
      );

      if ((response as any).status !== 200) {
        throw new Error('Failed to initiate password recovery');
      }

      setInitLoading(false);
      setShowForm(true);
    } catch (error: any) {
      toast.error(error.message || 'Password recovery initiation failed');
    } finally {
      setInitLoading(false);
    }
  }

  const handleResendPin = async () => {
    try {
      setResending(true);
      const payload = {
        change_type: 'recover',
        [usePhone ? 'phone' : 'email']: inputValue
      };

      await request(
        `/auth/v2/user/account/verification/${usePhone ? 'phone' : 'email'}/anon`,
        'POST',
        payload
      );

      setResent(true);
      setTimeout(() => setResent(false), 12000);
      toast.success('PIN sent successfully');
    } catch (error) {
      toast.error('Failed to resend PIN');
    } finally {
      setResending(false);
    }
  };

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setError('');
    const { name, value } = e.target;

    value ? (inputs[name as keyof Inputs] = value) : delete inputs[name as keyof Inputs];
    setInputs(prevState => ({ ...prevState, ...inputs }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!inputs.pin) return setError('Enter the PIN sent to your email/phone');
    if (inputs.pin.length !== 6) return setError('Invalid PIN');
    if (!inputs.password) return setError('Enter new password');
    if (!inputs.confirm) return setError('Confirm new password');
    if (inputs.password !== inputs.confirm) return setError('Password confirmation doesn\'t match');

    try {
      setLoading(true);
      const payload = {
        [usePhone ? 'phone' : 'email']: inputValue,
        pin: inputs.pin,
        new_password: inputs.password,
        confirm_password: inputs.confirm
      };

      const response = await request('/auth/v2/user/account/password/recover', 'POST', payload);

      if ((response as { status: number }).status !== 200) {
        throw new Error((response as { data: string }).data);
      }

      setLoading(false);
      setInputs({});
      toast.success("Password reset successfully");
      resetForm();
    } catch (error: any) {
      setError(error.message || 'Login failed')
      setTimeout(() => setError(""), 10000)
      toast.error(error.message || 'Password recovery failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      {(error) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      {/* Header Section */}
      <div className="space-y-4">
        <div>
          <a href="/" aria-label="FeelyFeely Admin" className="mb-6 flex justify-center">
            <BrandMark />
          </a>
          <h3 className="text-lg font-medium">Password Recovery</h3>
          <p className="text-sm text-gray-500">Reset your forgotten password</p>
        </div>

        {/* Initial Form */}
        {!showForm && (
          <div className="space-y-4">
            <div className="flex items-center justify-left gap-2">
              <input
                type="checkbox"
                id="usePhone"
                checked={usePhone}
                onChange={(e) => setUsePhone(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="usePhone" className="text-sm text-gray-600">
                Use Phone
              </label>
            </div>

            <input
              type={usePhone ? 'tel' : 'email'}
              placeholder={usePhone ? 'Phone number' : 'Email address'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="mb-4 text-right flex items-center justify-end">
              <button type="button"
                className="text-sm text-blue-900 hover:underline"
                onClick={() => onOpenChange('signin')}
              >
                Back to Login
              </button>
            </div>
            <button
              onClick={initiateProcess}
              disabled={initLoading || !inputValue.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {initLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Initiating...
                </>
              ) : (
                'Start Recovery'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Recovery Form */}
      {showForm && (
        <div className="space-y-6">
          <button
            onClick={() => setShowForm(false)}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>

          <div className="border rounded-lg p-6 space-y-6">
            {/* Password Requirements */}
            <div className="text-sm text-gray-600 space-y-2">
              <p><strong>Note:</strong> Please follow these password requirements:</p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>At least 12-16 characters long</li>
                <li>Include uppercase and lowercase letters, numbers, and special characters</li>
                <li>Avoid common words or simple patterns</li>
                <li>No personal information</li>
                <li>Avoid repeated characters or sequences</li>
              </ol>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-4 text-sm text-red-700 border border-red-300 rounded-lg bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-700" />
                <span>{error}</span>
              </div>
            )}

            {/* Recovery Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PIN Code*
                </label>
                <input
                  type="number"
                  name="pin"
                  maxLength={6}
                  value={inputs.pin || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoComplete="off"
                />
                <div className="mt-1 flex items-center justify-between">
                  <small className="text-gray-500">
                    6-digit PIN sent to {maskEmail(inputValue)}
                  </small>
                  {!resending && !resent && (
                    <button
                      type="button"
                      onClick={handleResendPin}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Resend PIN
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password*
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    minLength={12}
                    value={inputs.password || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password*
                </label>
                <input
                  type="password"
                  name="confirm"
                  minLength={12}
                  value={inputs.confirm || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {Object.keys(inputs).length > 0 && (
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordRecovery;
